import React, { useState, useEffect, useRef } from 'react';
import api, { prestazioniAPI } from '../../services/api';
import { templateAPI } from '../../services/templateApi';
import MedicoSelect from '../MedicoSelect';
import EditorTemplate from '../pazienti/EditorTemplate';
import { EditorDistinta, quantitaNonValide, righeDaSalvare }
    from './DistintaPrestazione';
import { magazzinoAPI } from '../../services/magazzinoApi';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { useAuthStore } from '../../store/useStore';
import { AlertTriangle } from 'lucide-react';
import SelettoreTemplate from '../pazienti/SelettoreTemplate';
import { perUnita } from '../../utils/denaro';
import '../../styles/PrestazioneForm.css';
import '../../styles/PrestazioneForm_consensi.css';
import '../../styles/DistintaPrestazione.css';

const UNITA_MISURA = ['PZ', 'SEDUTA', 'CONF', 'FIALA', 'UI', 'ML'];

const PrestazioneForm = ({ prestazione, onSave, onCancel }) => {
  const isEdit = Boolean(prestazione && prestazione.id);

  /* La distinta viaggia CON il modulo, anche quando la prestazione non
     esiste ancora: le righe stanno qui e partono insieme al resto in un
     salvataggio solo. Due chiamate dal frontend lascerebbero una
     prestazione senza distinta il giorno in cui la seconda fallisce. */
  const [righeDistinta, setRigheDistinta] = useState([]);
  /* In CREAZIONE i consensi collegati stanno qui e partono col resto:
     si collegano modelli che esistono gia', e quelli in uno stato non
     salvato ci stanno benissimo. In MODIFICA invece continua a valere
     quello che c'era - associazione e dissociazione immediate - perche'
     li' la prestazione esiste e la scrittura ha dove andare. */
  const [consensiNuovi, setConsensiNuovi] = useState([]);
  const [modelliDisponibili, setModelliDisponibili] = useState([]);
  const [prodottiMagazzino, setProdottiMagazzino] = useState([]);
  const [distintaPronta, setDistintaPronta] = useState(false);

  /* Il magazzino e' di admin e segreteria: a un medico la sezione non si
     mostra, perche' la rotta gli risponderebbe 403 e un pulsante che
     porta a un rifiuto insegna a diffidare dei pulsanti. */
  const ruolo = useAuthStore((state) => state.user?.ruolo);
  const puoMagazzino = ['admin', 'segreteria'].includes(ruolo);

  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    descrizione: '',
    descrizione_fattura: '',
    macro_area_id: '',
    aliquota_id: '',
    prezzo_indicativo: '',
    costo_prodotto: '',
    attivo: true,
    note: '',
    medici_ids: [],
    ammette_esenzione: false,
    unita_misura: 'PZ',
    ts_invio: false,
    ts_tipo_spesa: '',
    prenotabile: false,
    pacchetto_speciale: false,
    pacchetto_scadenza: '',
    durata_minuti: '',
    stanza_id: '',
    riservata: false,
    fatturabile_azienda: false,
    includi_nel_referto: false,
    /* Pacchetto a sedute (migration 273): la casella e' «il numero c'e'»,
       non un campo a se'. */
    pacchetto_a_sedute: false,
    sedute_numero: '',
    seduta_prestazione_id: '',
  });

  const [aliquote, setAliquote] = useState([]);
  /* Le voci che una seduta puo' eseguire: servizi, non pacchetti. */
  const [vociSeduta, setVociSeduta] = useState([]);
  const [tipiSpesa, setTipiSpesa] = useState([]);
  const [macroAree, setMacroAree] = useState([]);
  const rifDescrizioneFattura = useRef(null);
  const [errors, setErrors] = useState({});
  /* La tipologia non si sceglie nel modulo (i prodotti nascono dal
     magazzino): si legge dalla voce che si sta modificando. */
  const eBene = isEdit && prestazione?.tipologia === 'bene';
  const [stanze, setStanze] = useState([]);

  /* Le stanze servono solo dove il vincolo e' l'apparecchiatura:
     il laser CO2 e' uno solo, una visita si fa ovunque. */
  useEffect(() => {
    api.get('/agenda/stanze')
      .then((r) => setStanze(r.data.data?.stanze || r.data.data || []))
      .catch(() => setStanze([]));
  }, []);
  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  /* Chiudere con qualcosa di diverso da com'era chiede conferma: clic
     sullo sfondo, X e «Annulla» passano tutti da annulla(). Nel confronto
     entrano anche la distinta e i consensi da collegare, che partono col
     resto in un salvataggio solo. */
  const [versione, setVersione] = useState(0);
  const sporco = useSporco({ formData, righeDistinta, consensiNuovi }, versione);
  const chiedi = useUscitaProtetta(sporco);
  const annulla = () => { if (chiedi()) onCancel(); };

  // --- Consensi informati associati ---
  const [consensi, setConsensi] = useState([]);
  const [consensiLoading, setConsensiLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showSelettore, setShowSelettore] = useState(false);
  const [editorTemplateId, setEditorTemplateId] = useState(null);

  // Aliquote e tipi spesa arrivano dal database: quando cambiano
  // per legge si aggiorna una riga, non il codice.
  useEffect(() => {
    prestazioniAPI.getAliquote()
      .then((r) => setAliquote(r.data?.data?.aliquote || []))
      .catch(() => setGeneralError('Impossibile caricare i regimi IVA'));
    prestazioniAPI.getTipiSpesa()
      .then((r) => setTipiSpesa(r.data?.data?.tipiSpesa || []))
      .catch(() => {});
    prestazioniAPI.getMacroAree()
      .then((r) => setMacroAree(r.data?.data?.macro_aree || []))
      .catch(() => setGeneralError('Impossibile caricare le macro aree'));
    prestazioniAPI.getAll(true)
      .then((r) => setVociSeduta((r.data?.data?.prestazioni || [])
        .filter((v) => (v.tipologia || 'servizio') === 'servizio' && v.sedute_numero == null)))
      .catch(() => setVociSeduta([]));
  }, []);

  /* I modelli di consenso collegabili: servono solo in creazione, dove
     non c'e' ancora una prestazione a cui chiederli. */
  useEffect(() => {
    if (isEdit) return;
    templateAPI.getAll()
      .then((r) => setModelliDisponibili(
        (r.data?.data?.templates || r.data?.data || []).filter((t) => t.attivo !== false)))
      .catch(() => { /* la sezione resta vuota e lo dice */ });
  }, [isEdit]);

  /* Il catalogo di magazzino, e - in modifica - la distinta che c'e'
     gia'. In creazione non c'e' niente da leggere e si parte vuoti. */
  useEffect(() => {
    if (!puoMagazzino) { setDistintaPronta(true); return; }
    const chiamate = [magazzinoAPI.prodotti({})];
    if (isEdit) chiamate.push(magazzinoAPI.distinta(prestazione.id));
    Promise.all(chiamate)
      .then(([p, d]) => {
        setProdottiMagazzino(p.data?.data?.prodotti || []);
        if (d) {
          setRigheDistinta((d.data?.data?.distinta || []).map((x) => ({
            prodotto_id: x.prodotto_id,
            nome: x.nome,
            codice: x.codice,
            unita_misura: x.unita_misura,
            tracciabilita_rigorosa: x.tracciabilita_rigorosa,
            giacenza: x.giacenza,
            // I quattro decimali sono precisione di calcolo, non da leggere.
            quantita: String(parseFloat(x.quantita)),
          })));
        }
        setVersione((v) => v + 1);
      })
      .catch(() => setGeneralError('Impossibile caricare i prodotti di magazzino'))
      .finally(() => setDistintaPronta(true));
    // eslint-disable-next-line
  }, [isEdit, puoMagazzino]);

  useEffect(() => {
    if (isEdit) {
      setFormData({
        codice: prestazione.codice || '',
        nome: prestazione.nome || '',
        descrizione: prestazione.descrizione || '',
        descrizione_fattura: prestazione.descrizione_fattura || '',
        macro_area_id: prestazione.macro_area_id != null ? String(prestazione.macro_area_id) : '',
        aliquota_id: prestazione.aliquota_id || '',
        prezzo_indicativo:
          prestazione.prezzo_indicativo != null ? prestazione.prezzo_indicativo : '',
        costo_prodotto:
          prestazione.costo_prodotto != null ? prestazione.costo_prodotto : '',
        attivo: prestazione.attivo !== false,
        note: prestazione.note || '',
        medici_ids: (prestazione.medici || []).map((m) => m.id),
        ammette_esenzione: prestazione.ammette_esenzione === true,
        unita_misura: prestazione.unita_misura || 'PZ',
        ts_invio: prestazione.ts_invio === true,
        ts_tipo_spesa: prestazione.ts_tipo_spesa || '',
        prenotabile: prestazione.prenotabile === true,
        pacchetto_speciale: prestazione.pacchetto_speciale === true,
        pacchetto_scadenza: (prestazione.pacchetto_scadenza || '').toString().slice(0, 10),
        durata_minuti: prestazione.durata_minuti != null ? prestazione.durata_minuti : '',
        stanza_id: prestazione.stanza_id != null ? prestazione.stanza_id : '',
        riservata: prestazione.riservata === true,
        fatturabile_azienda: prestazione.fatturabile_azienda === true,
        includi_nel_referto: prestazione.includi_nel_referto === true,
        pacchetto_a_sedute: prestazione.sedute_numero != null,
        sedute_numero: prestazione.sedute_numero != null ? String(prestazione.sedute_numero) : '',
        seduta_prestazione_id: prestazione.seduta_prestazione_id != null ? String(prestazione.seduta_prestazione_id) : '',
      });
    }
    setVersione((v) => v + 1);
  }, [prestazione, isEdit]);

  const caricaConsensi = async () => {
    if (!isEdit) return;
    try {
      setConsensiLoading(true);
      const r = await templateAPI.getPerPrestazione(prestazione.id);
      setConsensi(r.data.data.template || []);
    } catch (err) {
      console.error('Errore caricamento consensi:', err);
    } finally {
      setConsensiLoading(false);
    }
  };

  useEffect(() => {
    caricaConsensi();
    // eslint-disable-next-line
  }, [prestazione, isEdit]);

  const handleDissocia = async (templateId) => {
    if (!window.confirm('Rimuovere questo consenso dalla prestazione?\n\nIl modello resta disponibile per le altre prestazioni.')) {
      return;
    }
    try {
      await templateAPI.dissocia(templateId, prestazione.id);
      caricaConsensi();
    } catch (err) {
      console.error('Errore rimozione consenso:', err);
      setGeneralError('Errore nella rimozione del consenso');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  /**
   * Inserisce {medico} dove si trova il cursore, non in coda: chi sta
   * scrivendo a meta' frase non deve spostare il testo a mano.
   */
  const inserisciSegnaposto = () => {
    const campo = rifDescrizioneFattura.current;
    const testo = formData.descrizione_fattura || '';
    const inizio = campo ? campo.selectionStart : testo.length;
    const fine = campo ? campo.selectionEnd : testo.length;

    const nuovo = testo.slice(0, inizio) + '{medico}' + testo.slice(fine);
    setFormData((prev) => ({ ...prev, descrizione_fattura: nuovo }));

    // Riporta il cursore dopo il segnaposto appena inserito.
    setTimeout(() => {
      if (campo) {
        campo.focus();
        const pos = inizio + '{medico}'.length;
        campo.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const aliquotaScelta = aliquote.find((a) => String(a.id) === String(formData.aliquota_id));

  // Avvisi su combinazioni che in cassa produrrebbero documenti
  // sbagliati. Non bloccano il salvataggio: segnalano.
  const avvisi = [];
  if (aliquotaScelta?.natura === 'esente' && !formData.ammette_esenzione) {
    avvisi.push('Regime esente ma la deroga non è ammessa: verifica che l\'esenzione sia dovuta di diritto e non richieda attestazione del medico.');
  }
  if (formData.ammette_esenzione && aliquotaScelta?.natura === 'esente') {
    avvisi.push('La voce è già esente: la spunta sulla deroga serve solo alle prestazioni imponibili che possono diventare esenti con attestazione.');
  }
  if (formData.ts_invio && formData.ts_tipo_spesa === 'IC' && aliquotaScelta?.natura === 'esente' && !formData.ammette_esenzione) {
    avvisi.push('Medicina estetica esente senza deroga ammessa: senza attestazione della finalità terapeutica l\'esenzione non è documentabile.');
  }

  const validate = () => {
    const e = {};
    if (!formData.nome.trim()) e.nome = 'Il nome è obbligatorio';
    if (!formData.aliquota_id) e.aliquota_id = 'Selezionare un regime IVA';
    if (!formData.macro_area_id) e.macro_area_id = 'Scegli la macro area';

    if (formData.prezzo_indicativo !== '' && formData.prezzo_indicativo !== null) {
      const p = parseFloat(formData.prezzo_indicativo);
      if (isNaN(p) || p < 0) e.prezzo_indicativo = 'Prezzo non valido';
    }

    if (formData.costo_prodotto !== '' && formData.costo_prodotto !== null) {
      const c = parseFloat(formData.costo_prodotto);
      if (isNaN(c) || c < 0) e.costo_prodotto = 'Costo non valido';
    }

    if (formData.ts_invio && !formData.ts_tipo_spesa) {
      e.ts_tipo_spesa = 'Indica il tipo di spesa per il Sistema TS';
    }
    // I beni non vanno al Sistema TS (proposta 15, 26/09/2026): lo pretende
    // anche il database.
    if (formData.ts_invio && eBene) {
      e.ts_tipo_spesa = 'I prodotti non si trasmettono al Sistema TS.';
    }
    if (formData.pacchetto_a_sedute) {
      const n = Number(formData.sedute_numero);
      if (!(Number.isInteger(n) && n >= 2)) e.sedute_numero = 'Il numero di sedute di un pacchetto è un intero, almeno 2.';
      if (!formData.seduta_prestazione_id) e.seduta_prestazione_id = 'Scegli la prestazione della seduta.';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError(null);
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        codice: formData.codice.trim() || null,
        nome: formData.nome.trim(),
        descrizione: formData.descrizione.trim() || null,
        descrizione_fattura: formData.descrizione_fattura.trim() || null,
        macro_area_id: formData.macro_area_id ? parseInt(formData.macro_area_id, 10) : null,
        aliquota_id: formData.aliquota_id,
        prezzo_indicativo:
          formData.prezzo_indicativo !== ''
            ? parseFloat(formData.prezzo_indicativo)
            : null,
        costo_prodotto:
          formData.costo_prodotto !== ''
            ? parseFloat(formData.costo_prodotto)
            : 0,
        attivo: formData.attivo,
        note: formData.note.trim() || null,
        /* Sempre: [] vuol dire voce generica. Il server toglie solo chi
           esce e aggiunge solo chi entra. */
        medici_ids: formData.medici_ids,
        ammette_esenzione: formData.ammette_esenzione,
        unita_misura: formData.unita_misura,
        ts_invio: formData.ts_invio,
        ts_tipo_spesa: formData.ts_tipo_spesa || null,
        prenotabile: formData.prenotabile,
        durata_minuti: formData.durata_minuti !== '' ? parseInt(formData.durata_minuti, 10) : null,
        stanza_id: formData.stanza_id !== '' ? parseInt(formData.stanza_id, 10) : null,
        /* Parte sempre: se e' uguale a quello in archivio il server la
           ignora, se cambia la accetta solo dall'amministrazione. */
        riservata: formData.riservata,
        fatturabile_azienda: formData.fatturabile_azienda,
        includi_nel_referto: formData.includi_nel_referto,
        /* Casella spenta: tutti e due vuoti, e la voce torna una voce come
           le altre (i pacchetti gia' venduti tengono il loro numero). */
        sedute_numero: formData.pacchetto_a_sedute ? parseInt(formData.sedute_numero, 10) : null,
        seduta_prestazione_id: formData.pacchetto_a_sedute ? parseInt(formData.seduta_prestazione_id, 10) : null,
      };

      /* La distinta va nello STESSO corpo, non in una seconda chiamata:
         il server crea prestazione e righe nella stessa transazione, e
         se la distinta fallisce non nasce niente. Due chiamate dal
         frontend lascerebbero una prestazione senza distinta il giorno
         in cui la seconda va male, e nessuno se ne accorgerebbe. */
      /* In creazione i consensi partono col resto: una transazione sola
         per prestazione, distinta e consensi. In modifica no - li' le
         associazioni si scrivono da sole quando si preme, e mandarle di
         nuovo qui le riscriverebbe senza motivo. */
      if (!isEdit) {
        payload.consensi = consensiNuovi.map((t) => t.id);
      }

      if (puoMagazzino) {
        if (quantitaNonValide(righeDistinta)) {
          setGeneralError('Le quantità dei prodotti devono essere numeri maggiori di zero');
          setSaving(false);
          return;
        }
        payload.prodotti = righeDaSalvare(righeDistinta);
      }

      if (isEdit) {
        await prestazioniAPI.update(prestazione.id, payload);
      } else {
        await prestazioniAPI.create(payload);
      }
      onSave();
    } catch (err) {
      console.error('Errore salvataggio prestazione:', err);
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        'Errore durante il salvataggio';
      setGeneralError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('modal-overlay') && !saving && !showEditor && !showSelettore) {
      annulla();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content prestazione-form-modal">
        <div className="modal-header">
          <h2>{isEdit ? 'Modifica prestazione' : 'Nuova prestazione'}</h2>
          <button
            type="button"
            className="btn-close"
            onClick={annulla}
            disabled={saving}
            aria-label="Chiudi"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="prestazione-form" noValidate>
          {generalError && <div className="error-message general">{generalError}</div>}

          {/* Le due sezioni corte, affiancate su schermo largo: e' la
              modifica che accorcia il modulo. Il contenitore e' l'unica
              cosa aggiunta al JSX - i due riquadri dentro sono quelli di
              prima, campo per campo. */}
          {/* DUE COLONNE VERE, non una griglia di riquadri: «Dati
              prestazione» e' alto 684px e «Regime fiscale» 430, quindi
              sotto il regime restavano 254px di niente e «Altro» andava
              a capo sotto tutti e due. Misurato il 22 settembre 2026. */}
          <div className="form-due-colonne">
          <div className="form-colonna">
          <fieldset className="form-section">
            <legend>Dati Prestazione</legend>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="codice">Codice interno</label>
                <input
                  id="codice"
                  type="text"
                  name="codice"
                  value={formData.codice}
                  onChange={handleChange}
                  placeholder="Es: BOTMEL"
                  maxLength={50}
                  disabled={saving}
                />
              </div>

              <div className="form-group flex-2">
                <label htmlFor="nome">
                  Nome <span className="required">*</span>
                </label>
                <input
                  id="nome"
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  className={errors.nome ? 'error' : ''}
                  placeholder="Nome interno, per la ricerca"
                  disabled={saving}
                  autoFocus
                />
                {errors.nome && <span className="field-error">{errors.nome}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="descrizione_fattura">Descrizione in fattura</label>
              <textarea
                id="descrizione_fattura"
                name="descrizione_fattura"
                ref={rifDescrizioneFattura}
                value={formData.descrizione_fattura}
                onChange={handleChange}
                rows={2}
                placeholder="Testo che vedrà il paziente. Es: Trattamento medico infiltrativo eseguito dal {medico}"
                disabled={saving}
              />
              <div style={{ marginTop: 6 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={inserisciSegnaposto}
                  disabled={saving}
                  title="Inserisce il segnaposto nel punto in cui si trova il cursore"
                  style={{ fontSize: 12, padding: '4px 10px' }}
                >
                  ＋ Inserisci {'{medico}'}
                </button>
              </div>
              <small className="form-hint">
                Il segnaposto <code>{'{medico}'}</code> viene sostituito in stampa col nominativo,
                così un cambio anagrafico non obbliga a correggere ogni voce.
                Se lasci vuoto viene usato il nome.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="descrizione">Descrizione interna</label>
              <textarea
                id="descrizione"
                name="descrizione"
                value={formData.descrizione}
                onChange={handleChange}
                rows={2}
                placeholder="Non compare in fattura..."
                disabled={saving}
              />
            </div>

            <div className="form-row">
              {/* La macro area e' quella che leggono i conteggi economici:
                  sostituisce la vecchia «Categoria», che nessun conteggio
                  leggeva (22 settembre 2026). Obbligatoria anche sul server. */}
              <div className="form-group">
                <label htmlFor="macro_area_id">Macro area *</label>
                <select
                  id="macro_area_id"
                  name="macro_area_id"
                  value={formData.macro_area_id}
                  onChange={handleChange}
                  className={errors.macro_area_id ? 'error' : ''}
                  disabled={saving}
                >
                  <option value="">Scegli…</option>
                  {macroAree.map((a) => (
                    <option key={a.id} value={String(a.id)}>{a.nome}</option>
                  ))}
                </select>
                {errors.macro_area_id && (
                  <span className="field-error">{errors.macro_area_id}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="prezzo_indicativo">Prezzo (€)</label>
                <input
                  id="prezzo_indicativo"
                  type="number"
                  name="prezzo_indicativo"
                  value={formData.prezzo_indicativo}
                  onChange={handleChange}
                  className={errors.prezzo_indicativo ? 'error' : ''}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  disabled={saving}
                />
                {errors.prezzo_indicativo && (
                  <span className="field-error">{errors.prezzo_indicativo}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="costo_prodotto">Costo prodotto (€)</label>
                <input
                  id="costo_prodotto"
                  type="number"
                  name="costo_prodotto"
                  value={formData.costo_prodotto}
                  onChange={handleChange}
                  className={errors.costo_prodotto ? 'error' : ''}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  disabled={saving}
                />
                {errors.costo_prodotto && (
                  <span className="field-error">{errors.costo_prodotto}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="unita_misura">Unità</label>
                <select
                  id="unita_misura"
                  name="unita_misura"
                  value={formData.unita_misura}
                  onChange={handleChange}
                  disabled={saving}
                >
                  {UNITA_MISURA.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Piu' medici sulla stessa voce (22 settembre 2026, migration
                202): prima un medico solo, e chi voleva la stessa prestazione
                per due medici doveva ripetere la voce. */}
            <div className="form-group">
              <label htmlFor="medico_select">Medici abilitati</label>
              <MedicoSelect
                multi
                name="medici_ids"
                value={formData.medici_ids}
                onChange={(ids) =>
                  setFormData((prev) => ({ ...prev, medici_ids: ids }))
                }
                placeholder="Nessuno (prestazione generica) — oppure cerca medici..."
              />
              <small className="form-hint">
                Nessun medico: la voce è generica e la può eseguire chiunque. Con uno
                o più medici, compare solo a loro nella procedura e sulle loro agende.
                Il prezzo è uno solo.
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="riservata" className="checkbox-label">
                <input
                  id="riservata"
                  type="checkbox"
                  name="riservata"
                  checked={formData.riservata}
                  onChange={handleChange}
                  disabled={saving || ruolo !== 'admin'}
                />
                <span>Medicina estetica — riservata</span>
              </label>
              <small className="campo-nota">
                Non è un&apos;etichetta: accende la riservatezza su ogni visita che
                contiene questa prestazione, anche su quelle già registrate. Togliere
                la spunta non rende visibili a tutti le visite di prima.
                {ruolo !== 'admin' && ' La cambia solo l\'amministrazione.'}
              </small>
            </div>

            {/* Medicina del lavoro (proposta 18, migration 266): la voce si
                fattura all'azienda del lavoratore collegato. E' un'eccezione
                alla regola «le prestazioni si fatturano al paziente», quindi
                la decide solo l'amministrazione. */}
            <div className="form-group checkbox-group">
              <label htmlFor="fatturabile_azienda" className="checkbox-label">
                <input
                  id="fatturabile_azienda"
                  type="checkbox"
                  name="fatturabile_azienda"
                  checked={formData.fatturabile_azienda}
                  onChange={handleChange}
                  disabled={saving || ruolo !== 'admin'}
                />
                <span>Fatturabile all&apos;azienda (medicina del lavoro)</span>
              </label>
              <small className="campo-nota">
                La visita si fattura all&apos;azienda di cui il paziente è lavoratore, alla data della
                visita (riquadro «Lavoro» della sua anagrafica). Niente Sistema TS: non è una spesa
                del lavoratore. Mai sui pacchetti.
                {ruolo !== 'admin' && ' La cambia solo l\'amministrazione.'}
              </small>
            </div>

            {/* Fase 2 del riordino (23 settembre 2026): il valore di PARTENZA
                della spunta «Includi nel referto» sul riquadro di questa
                prestazione, per tutto lo studio. Il medico la cambia per la
                singola visita. */}
            <div className="form-group checkbox-group">
              <label htmlFor="includi_nel_referto" className="checkbox-label">
                <input
                  id="includi_nel_referto"
                  type="checkbox"
                  name="includi_nel_referto"
                  checked={formData.includi_nel_referto}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span>Includi nel referto della visita</span>
              </label>
              <small className="campo-nota">
                Il valore di partenza della spunta sul riquadro di questa prestazione: se
                è accesa, la sua parte entra nel referto della visita. Il medico la cambia
                per la singola visita. Di serie è spenta, e il referto porta la sola parte
                «Visita».
              </small>
            </div>
          </fieldset>

          {/* ---------- Regime fiscale ---------- */}
          </div>

          <div className="form-colonna">
          <fieldset className="form-section">
            <legend>Regime fiscale</legend>

            <div className="form-row">
              <div className="form-group flex-2">
                <label htmlFor="aliquota_id">
                  Regime IVA <span className="required">*</span>
                </label>
                <select
                  id="aliquota_id"
                  name="aliquota_id"
                  value={formData.aliquota_id}
                  onChange={handleChange}
                  className={errors.aliquota_id ? 'error' : ''}
                  disabled={saving}
                >
                  <option value="">— seleziona —</option>
                  {aliquote.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.descrizione_stampa}
                      {a.natura_iva ? ` (${a.natura_iva})` : ''}
                    </option>
                  ))}
                </select>
                {errors.aliquota_id && <span className="field-error">{errors.aliquota_id}</span>}
                <small className="form-hint">
                  In fattura compare la dicitura per esteso: la sola percentuale non
                  basta a giustificare un'esenzione.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="ts_tipo_spesa">Tipo spesa Sistema TS</label>
                <select
                  id="ts_tipo_spesa"
                  name="ts_tipo_spesa"
                  value={formData.ts_tipo_spesa}
                  onChange={handleChange}
                  className={errors.ts_tipo_spesa ? 'error' : ''}
                  disabled={saving || !formData.ts_invio}
                >
                  <option value="">— nessuno —</option>
                  {tipiSpesa.map((t) => (
                    <option key={t.codice} value={t.codice}>
                      {t.codice} — {t.descrizione}
                    </option>
                  ))}
                </select>
                {errors.ts_tipo_spesa && <span className="field-error">{errors.ts_tipo_spesa}</span>}
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="pacchetto_speciale" className="checkbox-label">
                <input
                  id="pacchetto_speciale"
                  type="checkbox"
                  name="pacchetto_speciale"
                  checked={formData.pacchetto_speciale}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span>Voce con scadenza</span>
              </label>
              <small className="campo-nota">
                Finisce nella schermata <b>Voci con scadenza</b>, dove si tiene
                d&apos;occhio la data di termine. Per tutto il resto resta una voce
                come le altre: si prenota, si esegue, si fattura. In fattura
                l&apos;etichetta non compare.
              </small>
            </div>

            {formData.pacchetto_speciale && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pacchetto_scadenza">Scade il</label>
                  <input
                    id="pacchetto_scadenza"
                    type="date"
                    name="pacchetto_scadenza"
                    value={formData.pacchetto_scadenza}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  <small className="campo-nota">
                    Lasciandolo vuoto il pacchetto non ha termine. Togliendo la
                    spunta qui sopra, la data si cancella.
                  </small>
                </div>
              </div>
            )}

            <div className="form-group checkbox-group">
              <label htmlFor="prenotabile" className="checkbox-label">
                <input
                  id="prenotabile"
                  type="checkbox"
                  name="prenotabile"
                  checked={formData.prenotabile}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span>Prenotabile in agenda</span>
              </label>
            </div>

            {formData.prenotabile && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="durata_minuti">Durata (minuti)</label>
                  <input
                    id="durata_minuti"
                    type="number"
                    name="durata_minuti"
                    min="5"
                    max="480"
                    step="5"
                    value={formData.durata_minuti}
                    onChange={handleChange}
                    disabled={saving}
                    placeholder="30"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="stanza_id">Stanza o apparecchiatura</label>
                  <select
                    id="stanza_id"
                    name="stanza_id"
                    value={formData.stanza_id}
                    onChange={handleChange}
                    disabled={saving}
                  >
                    <option value="">nessun vincolo</option>
                    {stanze.map((s) => (
                      <option key={s.id} value={s.id}>{s.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="form-group checkbox-group">
              <label htmlFor="ts_invio" className="checkbox-label">
                <input
                  id="ts_invio"
                  type="checkbox"
                  name="ts_invio"
                  checked={formData.ts_invio}
                  onChange={handleChange}
                  disabled={saving || eBene}
                />
                <span>Da trasmettere al Sistema Tessera Sanitaria</span>
              </label>
              {eBene && (
                <small className="form-hint">I prodotti non si trasmettono al Sistema TS.</small>
              )}
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="ammette_esenzione" className="checkbox-label">
                <input
                  id="ammette_esenzione"
                  type="checkbox"
                  name="ammette_esenzione"
                  checked={formData.ammette_esenzione}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span>Può essere fatturata in esenzione con attestazione del medico</span>
              </label>
              <small className="form-hint">
                Solo per le voci imponibili che diventano esenti quando il medico
                documenta la finalità terapeutica. Alla cassa l'esenzione resterà
                bloccata finché non esiste l'attestazione firmata.
              </small>
            </div>

            {avvisi.length > 0 && (
              <div className="form-group">
                {avvisi.map((a, i) => (
                  <p key={i} className="form-hint" style={{ color: '#a16207' }}>{a}</p>
                ))}
              </div>
            )}
          </fieldset>

          {/* ---------- Pacchetto a sedute (Alex, 30/09/2026) ----------
              Il numero di sedute sta sulla voce e si copia sul pacchetto
              alla vendita: cambiarlo qui non tocca quelli gia' venduti.
              L'omogeneita' con la voce della seduta la controlla anche il
              database, e il suo messaggio arriva qui com'e'. */}
          {!eBene && (
          <fieldset className="form-section">
            <legend>Pacchetto</legend>
            <div className="form-group checkbox-group">
              <label htmlFor="pacchetto_a_sedute" className="checkbox-label">
                <input
                  id="pacchetto_a_sedute"
                  type="checkbox"
                  name="pacchetto_a_sedute"
                  checked={formData.pacchetto_a_sedute}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span>Pacchetto a sedute</span>
              </label>
            </div>
            {formData.pacchetto_a_sedute && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="sedute_numero">Numero di sedute *</label>
                    <input
                      id="sedute_numero"
                      type="number"
                      name="sedute_numero"
                      min="2"
                      step="1"
                      value={formData.sedute_numero}
                      onChange={handleChange}
                      className={errors.sedute_numero ? 'error' : ''}
                      disabled={saving}
                    />
                    {errors.sedute_numero && <span className="field-error">{errors.sedute_numero}</span>}
                  </div>
                  <div className="form-group">
                    <label>Valore di una seduta</label>
                    <input
                      type="text"
                      readOnly
                      value={(() => {
                        const n = Number(formData.sedute_numero);
                        const p = parseFloat(formData.prezzo_indicativo);
                        if (!(n >= 2) || !(p > 0)) return '—';
                        /* Come il database: prezzo diviso sedute a quattro
                           decimali, arrotondato ai centesimi solo alla fine. */
                        const v = perUnita(Math.round((p / n) * 10000) / 10000, 1);
                        return v.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
                      })()}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="seduta_prestazione_id">Prestazione della seduta *</label>
                  <select
                    id="seduta_prestazione_id"
                    name="seduta_prestazione_id"
                    value={formData.seduta_prestazione_id}
                    onChange={handleChange}
                    className={errors.seduta_prestazione_id ? 'error' : ''}
                    disabled={saving}
                  >
                    <option value="">— scegli —</option>
                    {vociSeduta.filter((v) => !isEdit || v.id !== prestazione.id).map((v) => {
                      const uguale = String(v.aliquota_id) === String(formData.aliquota_id)
                        && v.ts_invio === formData.ts_invio
                        && (v.ts_tipo_spesa || '') === (formData.ts_invio ? formData.ts_tipo_spesa || '' : '')
                        && v.ammette_esenzione === formData.ammette_esenzione;
                      return (
                        <option key={v.id} value={v.id}>
                          {v.nome}{uguale ? '' : ' — regole di IVA o TS diverse'}
                        </option>
                      );
                    })}
                  </select>
                  {errors.seduta_prestazione_id && <span className="field-error">{errors.seduta_prestazione_id}</span>}
                  <small className="form-hint">
                    La voce che ogni seduta esegue, di solito la stessa prestazione singola. Deve avere lo stesso
                    regime IVA e lo stesso tipo di spesa per il Sistema TS di questa voce: un pacchetto si fa solo
                    con sedute omogenee.
                  </small>
                </div>
                <small className="form-hint">
                  Il pacchetto scade dodici mesi dopo la prima fattura emessa. Il numero di sedute e la prestazione
                  della seduta si copiano sul pacchetto quando si vende: cambiarli qui non cambia i pacchetti già venduti.
                </small>
              </>
            )}
          </fieldset>
          )}

          {/* «Altro» ENTRA nella griglia a due colonne: contiene le note
              interne e la spunta «Prestazione attiva», e a tutta larghezza
              allungava la pagina per niente (misurato il 22 settembre
              2026). Sotto i 1100px la griglia si impila da sola e torna a
              una colonna. */}
          <fieldset className="form-section">
            <legend>Altro</legend>

            <div className="form-group">
              <label htmlFor="note">Note interne</label>
              <textarea
                id="note"
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows={2}
                placeholder="Note opzionali..."
                disabled={saving}
              />
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="attivo" className="checkbox-label">
                <input
                  id="attivo"
                  type="checkbox"
                  name="attivo"
                  checked={formData.attivo}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span>Prestazione attiva</span>
              </label>
            </div>
          </fieldset>
          </div>

          </div>

          {/* ---------- Prodotti e magazzino ----------
               Qui e non solo nell'icona dell'elenco: chi apre «Nuova
               prestazione» e non trova niente conclude che associare un
               prodotto non si possa fare, ed e' successo davvero. */}
          {puoMagazzino && (
            <fieldset className="form-section">
              <legend>Prodotti e magazzino</legend>

              {/* Una prestazione estetica senza prodotti non e' «non
                  consuma niente»: e' «non lo sappiamo ancora», ed e' il
                  posto dove chi la compila lo vede per primo.
                  Leggeva la categoria «Medicina estetica»; tolta la
                  categoria (22 settembre 2026) sarebbe rimasto muto per
                  sempre. La spunta riservata coincide con quella categoria
                  su tutte le 190 voci. */}
              {formData.riservata
                && righeDistinta.length === 0 && (
                <p className="pf-distinta-avviso">
                  <AlertTriangle size={15} aria-hidden="true" />
                  <span>
                    Prestazione di medicina estetica senza nessun prodotto
                    collegato. Se ne consuma uno — un filler, una tossina —
                    il numero di lotto non ha dove andare, e per un
                    dispositivo impiantabile la risalibilità al paziente è
                    un obbligo di legge.
                  </span>
                </p>
              )}

              <EditorDistinta
                righe={righeDistinta}
                onCambia={setRigheDistinta}
                prodotti={prodottiMagazzino}
                caricamento={!distintaPronta}
              />
            </fieldset>
          )}

          {/* ---------- Consensi informati ---------- */}
          <fieldset className="form-section">
            <legend>Consensi informati</legend>

            {!isEdit ? (
              <>
                {consensiNuovi.length === 0 ? (
                  <p className="form-hint">
                    Nessun consenso collegato. Quando questa prestazione viene
                    eseguita non verrà proposta alcuna firma.
                  </p>
                ) : (
                  <ul className="consensi-lista">
                    {consensiNuovi.map((t) => (
                      <li key={t.id} className="consenso-voce">
                        <div className="consenso-testo">
                          <strong>{t.titolo}</strong>
                          <small>
                            {t.categoria || 'Senza categoria'}
                            {' · '}
                            {t.medico_cognome
                              ? `Dott./Dott.ssa ${t.medico_cognome}`
                              : 'Modello di struttura'}
                            {' · v'}{t.versione}
                          </small>
                        </div>
                        <div className="consenso-azioni">
                          <button
                            type="button"
                            className="btn-link btn-link-danger"
                            onClick={() => setConsensiNuovi((p) => p.filter((x) => x.id !== t.id))}
                          >
                            Rimuovi
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                <label className="form-group">
                  <span>Collega un modello esistente</span>
                  <select
                    value=""
                    disabled={saving}
                    onChange={(e) => {
                      const t = modelliDisponibili.find((x) => String(x.id) === e.target.value);
                      if (t) setConsensiNuovi((p) => [...p, t]);
                    }}
                  >
                    <option value="">
                      {modelliDisponibili.length ? 'Scegli un modello…' : 'Nessun modello disponibile'}
                    </option>
                    {modelliDisponibili
                      .filter((t) => !consensiNuovi.some((x) => x.id === t.id))
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.titolo}{t.versione ? ` (v${t.versione})` : ''}
                        </option>
                      ))}
                  </select>
                </label>

                {/* Perche' qui non si crea un modello nuovo. Detto a
                    schermo e non solo nel codice: chi non lo trova deve
                    sapere che non e' una dimenticanza. */}
                <p className="form-hint">
                  Qui si <b>collegano modelli che esistono già</b>. Per scriverne
                  uno nuovo servono le sue versioni e la sua firma: è un
                  documento, non un campo di questo modulo, e si crea riaprendo
                  la prestazione in modifica — oppure da <b>Consensi</b>.
                </p>
              </>
            ) : (
              <>
                {consensiLoading ? (
                  <p className="form-hint">Caricamento consensi…</p>
                ) : consensi.length === 0 ? (
                  <p className="form-hint">
                    Nessun consenso associato. Quando questa prestazione viene eseguita non verrà
                    proposta alcuna firma.
                  </p>
                ) : (
                  <ul className="consensi-lista">
                    {consensi.map((t) => (
                      <li key={t.id} className="consenso-voce">
                        <div className="consenso-testo">
                          <strong>{t.titolo}</strong>
                          <small>
                            {t.categoria || 'Senza categoria'}
                            {' · '}
                            {t.medico_cognome
                              ? `Dott./Dott.ssa ${t.medico_cognome}`
                              : 'Modello di struttura'}
                            {' · v'}{t.versione}
                            {t.obbligatorio === false ? ' · facoltativo' : ''}
                          </small>
                        </div>
                        <div className="consenso-azioni">
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => { setEditorTemplateId(t.id); setShowEditor(true); }}
                          >
                            Modifica
                          </button>
                          <button
                            type="button"
                            className="btn-link btn-link-danger"
                            onClick={() => handleDissocia(t.id)}
                          >
                            Rimuovi
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="consensi-azioni">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowSelettore(true)}
                    disabled={saving}
                  >
                    Associa consenso
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => { setEditorTemplateId(null); setShowEditor(true); }}
                    disabled={saving}
                  >
                    Crea consenso
                  </button>
                </div>
              </>
            )}
          </fieldset>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={annulla} disabled={saving}>
              Annulla
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Salvataggio...' : isEdit ? 'Salva modifiche' : 'Crea prestazione'}
            </button>
          </div>
        </form>
      </div>

      {showEditor && (
        <EditorTemplate
          templateId={editorTemplateId}
          prestazioneId={isEdit ? prestazione.id : null}
          onClose={() => setShowEditor(false)}
          onSuccess={caricaConsensi}
        />
      )}

      {showSelettore && (
        <SelettoreTemplate
          prestazioneId={prestazione.id}
          giaAssociati={consensi.map((t) => t.id)}
          onClose={() => setShowSelettore(false)}
          onSuccess={caricaConsensi}
        />
      )}
    </div>
  );
};

export default PrestazioneForm;
